import api from './api.service';

export enum CostingType {
  SIMPLE = 'Simple',
  DETAILED = 'Detailed'
}

export enum LineItemStatus {
  DRAFT = 'Draft',
  CALCULATED = 'Calculated',
  APPROVED = 'Approved'
}

export interface EstimationCategory {
  id: string;
  estimationId: string;
  categoryName: string;
  sequenceNumber: number;
  categoryTotal: number;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EstimationLineItem {
  id: string;
  estimationId: string;
  categoryId?: string;
  lineNumber: number;
  itemCode?: string;
  itemName: string;
  description?: string;
  quantity: number;
  uom: string;
  costingType: CostingType;
  manualUnitCost?: number;
  materialCost: number;
  laborCost: number;
  overheadCost: number;
  toolingCost: number;
  operationCost: number;
  totalUnitCost: number;
  lineTotal: number;
  marginPercentage: number;
  unitSellingPrice: number;
  lineSellingTotal: number;
  leadTimeDays: number;
  supplierId?: string;
  supplierName?: string;
  notes?: string;
  status: LineItemStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MultiItemEstimationResponse {
  estimationId: string;
  /** Some endpoints return id at root */
  id?: string;
  /** Wrapped API shape (legacy) */
  data?: {
    id?: string;
    estimationId?: string;
    estimation?: MultiItemEstimationResponse['estimation'];
    categories?: MultiItemEstimationResponse['categories'];
    summary?: MultiItemEstimationResponse['summary'];
  };
  estimation: {
    id: string;
    estimationNumber: string;
    estimationName: string;
    customerId?: string;
    customerName?: string;
    projectId?: string;
    projectName?: string;
    department_id?: string | null;
    currency: string;
    marginPercentage: number;
    status: string;
    sourceBoqId?: string;
    sourceBoqNumber?: string;
    opportunityId?: string;
    opportunityNumber?: string;
    opportunityName?: string;
  };
  categories: Array<{
    category: EstimationCategory;
    lineItems: EstimationLineItem[];
  }>;
  summary: {
    totalCategories: number;
    totalLineItems: number;
    simpleItems: number;
    detailedItems: number;
    totalCost: number;
    totalSellingPrice: number;
  };
}

export interface CreateCategoryRequest {
  categoryName: string;
  sequenceNumber?: number;
  notes?: string;
}

export interface CreateLineItemRequest {
  categoryId?: string;
  itemCode?: string;
  itemName: string;
  description?: string;
  quantity: number;
  uom: string;
  costingType: CostingType;
  manualUnitCost?: number;
  marginPercentage?: number;
  supplierId?: string;
  supplierName?: string;
  leadTimeDays?: number;
  notes?: string;
}

class MultiItemEstimationService {
  // Multi-item estimation management
  async createMultiItemEstimation(estimationId: string, data: any) {
    const response = await api.post(`/estimations/${estimationId}/multi-item`, data);
    return response.data;
  }

  async getMultiItemEstimation(estimationId: string): Promise<MultiItemEstimationResponse> {
    const response = await api.get(`/estimations/${estimationId}/multi-item`);
    console.log('🔧 getMultiItemEstimation - response.data:', response.data);
    console.log('🔧 getMultiItemEstimation - response.data.data:', response.data.data);
    // Handle both wrapped and unwrapped responses
    return response.data.data || response.data;
  }

  async getEstimationSummary(estimationId: string) {
    const response = await api.get(`/estimations/${estimationId}/multi-item/summary`);
    return response.data;
  }

  async recalculateEstimation(estimationId: string): Promise<MultiItemEstimationResponse> {
    const response = await api.post(`/estimations/${estimationId}/multi-item/recalculate`);
    return response.data.data;
  }

  async duplicateEstimation(sourceEstimationId: string, targetEstimationId: string) {
    const response = await api.post(`/estimations/${sourceEstimationId}/duplicate`, {
      targetEstimationId
    });
    return response.data;
  }

  // Category management
  async addCategory(estimationId: string, data: CreateCategoryRequest): Promise<EstimationCategory> {
    const response = await api.post(`/estimations/${estimationId}/categories`, data);
    return response.data.data;
  }

  async getCategories(estimationId: string): Promise<EstimationCategory[]> {
    const response = await api.get(`/estimations/${estimationId}/categories`);
    return response.data.data;
  }

  async updateCategory(categoryId: string, data: Partial<CreateCategoryRequest>): Promise<EstimationCategory> {
    const response = await api.put(`/categories/${categoryId}`, data);
    return response.data.data;
  }

  async deleteCategory(categoryId: string, deleteLineItems: boolean = false) {
    const response = await api.delete(`/categories/${categoryId}`, {
      params: { deleteLineItems }
    });
    return response.data;
  }

  async reorderCategories(estimationId: string, categoryOrders: { id: string; sequenceNumber: number }[]) {
    const response = await api.put(`/estimations/${estimationId}/categories/reorder`, {
      categoryOrders
    });
    return response.data;
  }

  // Line item management
  async addLineItem(estimationId: string, data: CreateLineItemRequest): Promise<EstimationLineItem> {
    const response = await api.post(`/estimations/${estimationId}/line-items`, data);
    return response.data.data;
  }

  async getLineItems(estimationId: string, categoryId?: string): Promise<EstimationLineItem[]> {
    const response = await api.get(`/estimations/${estimationId}/line-items`, {
      params: { categoryId }
    });
    return response.data.data;
  }

  async getLineItemById(lineItemId: string): Promise<EstimationLineItem> {
    const response = await api.get(`/line-items/${lineItemId}`);
    return response.data.data;
  }

  async updateLineItem(lineItemId: string, data: Partial<CreateLineItemRequest>): Promise<EstimationLineItem> {
    const response = await api.put(`/line-items/${lineItemId}`, data);
    return response.data.data;
  }

  async deleteLineItem(lineItemId: string) {
    const response = await api.delete(`/line-items/${lineItemId}`);
    return response.data;
  }

  async calculateDetailedCosting(lineItemId: string): Promise<EstimationLineItem> {
    const response = await api.post(`/line-items/${lineItemId}/calculate`);
    return response.data.data;
  }

  async changeCostingType(lineItemId: string, costingType: CostingType): Promise<EstimationLineItem> {
    const response = await api.put(`/line-items/${lineItemId}/costing-type`, {
      costingType
    });
    return response.data.data;
  }

  async getLineItemSummary(lineItemId: string) {
    const response = await api.get(`/line-items/${lineItemId}/summary`);
    return response.data;
  }

  async moveLineItemToCategory(lineItemId: string, categoryId?: string): Promise<EstimationLineItem> {
    const response = await api.put(`/line-items/${lineItemId}/move`, {
      categoryId
    });
    return response.data.data;
  }

  async reorderLineItems(estimationId: string, lineOrders: { id: string; lineNumber: number }[]) {
    const response = await api.put(`/estimations/${estimationId}/line-items/reorder`, {
      lineOrders
    });
    return response.data;
  }

  // Create estimation from BOQ
  async createFromBOQ(
    boqId: string,
    options?: { opportunityId?: string }
  ): Promise<MultiItemEstimationResponse> {
    const params =
      options?.opportunityId != null && options.opportunityId !== ''
        ? { opportunity_id: options.opportunityId }
        : undefined;
    const response = await api.post(`/estimations/multi-item/from-boq/${boqId}`, {}, { params });
    return response.data?.data || response.data;
  }

  /** Link or clear CRM opportunity on a multi-item estimation */
  async patchEstimationOpportunity(
    estimationId: string,
    opportunity_id: string | null
  ): Promise<MultiItemEstimationResponse> {
    const response = await api.patch(`/estimations/${estimationId}/opportunity`, { opportunity_id });
    return response.data?.data || response.data;
  }

  async syncOpportunityDocuments(estimationId: string): Promise<{ copied: number }> {
    const response = await api.post(`/estimations/${estimationId}/sync-opportunity-documents`, {});
    const payload = (response.data as { data?: { copied?: number }; copied?: number }) ?? {};
    const n = Number(payload.data?.copied ?? payload.copied);
    return { copied: Number.isFinite(n) ? n : 0 };
  }
}

export default new MultiItemEstimationService();

